import React, {
  useState, useEffect, useContext, useMemo,
} from 'react';
import {
  Layout, PageHeader,
  Tabs, Button,
} from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,

  BugOutlined,
} from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import {
  Switch, Route, Redirect, useLocation, useHistory,
} from 'react-router-dom';
import {
  HeaderSocialLinks, HeaderDropdownOptions, GlobalFooter, SideMenu,
  ErrorFallback, RecentActivity, BirthdayAnnouncement,
  FullPageLoader,
} from 'components';

import PageNotFound from 'pages/PageNotFound/PageNotFound';
import AccessDenied from 'pages/AccessDenied/AccessDenied';
import ComingSoon from 'pages/ComingSoon/ComingSoon';
import './DashboardLayout.less';
import logo from 'assets/logo.svg';
import routeConfig, { getPermissionGatedRoutes } from 'config/routes';
import { IT_SUPPORT_MONDAY_FORM_URL } from 'pages/dashboard/Home/links';
import { AuthContext } from 'util/Auth';
import { PermissionsContext, userCanAccessRoute } from 'util/PermissionsContext';
import { ErrorBoundary } from 'react-error-boundary';
import { PageTabsProvider } from 'util/PageTabs';
import queryString from 'query-string';
import { MiscDataProvider, MiscDataContext } from 'util/MiscData';
import { PermissionGatedRoutesContext } from 'util/PermissionGatedRoutesContext';

import * as api from 'util/api';
import * as usage from 'util/usage';
import { isNumber } from 'lodash';

const { TabPane } = Tabs;

const { Header } = Layout;

// Recursively renders routes based on route configuration file
const renderRoutes = (parentPath, items, userIsAdmin, userIsExternal, userEmail, permissions) => {
  const perms = permissions || [];
  const result = items.reduce((accumulator, item) => {
    // Menu-only entries are sidebar mirrors of real routes — skip route registration
    if (item.menuOnly) {
      return accumulator;
    }

    let routes = [];

    let path = parentPath;
    if (item.pathParam) {
      path += `/:${item.pathParam}`;
    }
    if (item.id !== 'view') {
      path += `/${item.id}`;
    }

    // if (item.tabs) {
    //   routes.push(
    //     <Route
    //       path={`${path}`}
    //       render={({ match, location, history }) => {
    //         if (item.comingSoon && !item.earlyAccessEmails.includes(userEmail)) {
    //           return ComingSoon;
    //         }
    //         return (
    //           ((item.requiresAdmin && !userIsAdmin) || (!item.allowExternal && userIsExternal)
    //           ) ? AccessDenied : (
    //             <>
    //               {/* Redirect to first tab if on the base path */}
    //               {location.pathname === path && <Redirect to={`${path}/${item.tabs[0].id}`} />}
    //               <Switch>
    //                 <Tabs
    //                   defaultActiveKey={match.params.tab}
    //                   onChange={(key) => {
    //                     history.push(`${path}/${key}`);
    //                   }}
    //                 >
    //                   {item.tabs.map(({ id, title, Component }) => (
    //                     <TabPane key={id} tab={title}>
    //                       <Component />
    //                     </TabPane>
    //                   ))}
    //                 </Tabs>
    //               </Switch>
    //             </>
    //             ));
    //       }}
    //     />,
    //   );
    // } else

    if (item.Component) {
      let { Component } = item;
      if (item.comingSoon && !item.earlyAccessEmails?.includes(userEmail)) {
        Component = ComingSoon;
      } else if (!item.allowExternal && userIsExternal) {
        Component = AccessDenied;
      } else if (!userCanAccessRoute(item, userEmail, perms, userIsAdmin)) {
        Component = AccessDenied;
      }
      routes.push(
        item.hasNestedSwitch && item.subItems ? (
          <Route
            key={path}
            path={path}
            component={Component}
          />
        ) : (
          <Route
            key={path}
            exact
            path={path}
            component={Component}
          />
        ),
      );
    }
    if (item.subItems) {
      routes = [
        ...routes,
        ...renderRoutes(
          path,
          item.subItems,
          userIsAdmin,
          userIsExternal,
          userEmail,
          perms,
        ),
      ];
    }
    return [...accumulator, ...routes];
  }, []);
  return result;
};

const PageContent = ({ currentUser, permissions, hideFooter }) => (
  <div
    id="scrollable-page-area-container"
    className={hideFooter ? 'scrollable-page-area-container--flush' : undefined}
  >
    <div
      id="scrollable-page-area"
      className={hideFooter ? 'scrollable-page-area--flush' : undefined}
    >
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={api.reportError}>
        <main>
          <Switch>
            <Redirect
              exact
              from="/dashboard/product-database/product-cache"
              to="/dashboard/product-database/monday-integration?tab=product-cache"
            />
            <Redirect
              exact
              from="/dashboard/create/qr-codes"
              to="/dashboard/qr-codes"
            />
            <Redirect
              exact
              from="/dashboard/create/landing-page"
              to="/dashboard/landing-page"
            />
            <Redirect
              exact
              from="/dashboard/create/url-shortener"
              to="/dashboard/url-shortener"
            />
            <Redirect
              exact
              from="/dashboard/create/quick-renderer"
              to="/dashboard/quick-renderer"
            />
            {renderRoutes('/dashboard', routeConfig, currentUser?.admin, currentUser?.external, currentUser?.email, permissions)}
            <Redirect exact from="/dashboard" to={currentUser?.external ? '/dashboard/beer-bible' : '/dashboard/home'} />
            <Route path="*" component={PageNotFound} />
          </Switch>
        </main>
      </ErrorBoundary>
      {!hideFooter && <GlobalFooter />}
    </div>
  </div>
);

const DashboardLayout = () => {
  const [pageHeaderDetails, setPageHeaderDetails] = useState(null);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [tabBarExtra, setTabBarExtra] = useState(null);

  const onReportBugClicked = () => {
    window.open(IT_SUPPORT_MONDAY_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  const miscData = useContext(MiscDataContext);

  const isMobile = useMediaQuery({
    query: '(max-width: 576px)', // TODO: Change to sm breakpoint
  });

  const closeSideMenu = useMediaQuery({
    query: '(max-width: 992px)', // TODO: Change to lg breakpoint
  });

  const [sideMenuOpen, setSideMenuOpen] = useState(!closeSideMenu);

  const feedbackButtonsInHeader = useMediaQuery({
    query: `(min-width: ${sideMenuOpen ? '815' : '625'}px)`,
  });

  // Handle automatic collapse and expanding of side menu based on screen size.
  useEffect(() => {
    if (closeSideMenu && sideMenuOpen) {
      setSideMenuOpen(false);
    } else if (!closeSideMenu && !sideMenuOpen) {
      setSideMenuOpen(true);
    }
  }, [closeSideMenu]);

  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    usage.start();
    return () => usage.stop();
  }, []);

  const history = useHistory();

  const { currentUser } = useContext(AuthContext);
  const { permissions } = useContext(PermissionsContext);

  const [showLegacyPages, setShowLegacyPages] = useState(
    () => localStorage.getItem(`${currentUser?.uid}.showLegacyPages`) === 'true',
  );

  const onToggleLegacy = (enabled) => {
    if (currentUser?.uid) {
      localStorage.setItem(`${currentUser.uid}.showLegacyPages`, String(enabled));
    }
    setShowLegacyPages(enabled);
  };

  // When the pathname changes, handle setting page title details and track route
  useEffect(() => {
    if (pathname === '/dashboard') {
      // Wait for redirect to specific path within dashboard
      return;
    }
    // Separate the location pathname into separate parts (excluding 'dashboard')
    const pathParts = pathname.substring('/dashboard/'.length).split('/');

    const route = pathParts.reduce((currentRouteItem, pathPart) => {
      // Find the root route item on first iteration
      if (!currentRouteItem) {
        return routeConfig.find(({ id }) => id === pathPart);
      }

      // Skip the path part if an ID (number)
      if (/^-?\d+$/.test(pathPart)) {
        return currentRouteItem;
      }

      if (currentRouteItem.subItems) {
        return currentRouteItem.subItems?.find(({ id }) => id === pathPart);
      }

      return currentRouteItem;
    }, null);

    if (route) {
      const headerTitle = route.title ?? route.menuLabel;
      setPageHeaderDetails({
        title: headerTitle == null ? '' : String(headerTitle),
        description: route.description,
        backPath: route.backPath,
      });
    }

    setCurrentRoute(route);

    // Track from the route resolved in this pass. Reading currentRoute here
    // would still be the previous page and would log its name on this path.
    const tracked = usage.pagePath(pathname, location.search);
    if (tracked) usage.trackPage(tracked);
  }, [pathname, location.search]);

  // When the route changes, check for and apply any existing tab query param
  useEffect(() => {
    if (currentRoute?.tabs) {
      const queryParams = queryString.parse(location.search);
      const tabParam = queryParams[currentRoute.tabQueryLabel || 'tab'];
      const matchedTab = currentRoute.tabs.find(({ id, aliases }) => (
        id === tabParam || (aliases || []).includes(tabParam)
      ));

      // Set the provided tab based on the param if it exists and is valid
      // otherwise set the first tab as default. Aliases map to the canonical id.
      setActiveTab(matchedTab ? matchedTab.id : currentRoute.tabs[0].id);
    } else {
      // Clear the active tab if the page does not have tabs
      setActiveTab(null);
    }
  }, [currentRoute]);

  // Update the tab query param whenever the active tab changes
  // TODO: Handle future case where other query params may be used
  useEffect(() => {
    if (currentRoute) {
      if (activeTab) {
        // Set the active tab as the query param
        history.push({
          pathname,
          search: queryString.stringify({
            [currentRoute.tabQueryLabel || 'tab']: activeTab,
          }),
        });
      } else {
        // No active tab, so clear the query param
        history.push({
          pathname,
          search: null,
        });
      }
    }
  }, [activeTab]);

  const permissionGatedRoutes = useMemo(() => getPermissionGatedRoutes(routeConfig), []);

  return (
    <PermissionGatedRoutesContext.Provider value={permissionGatedRoutes}>
      <MiscDataProvider>
        <Layout>
          <SideMenu
            open={sideMenuOpen}
            isMobile={isMobile}
            onClose={() => setSideMenuOpen(false)}
            showLegacyPages={showLegacyPages}
          />
          <Layout>
            <Header className="site-header">
              { isMobile && (
              <div className="site-header-logo-overlay">
                <img src={logo} alt="logo" />
              </div>
              ) }
              <div
                className="side-menu-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setSideMenuOpen(!sideMenuOpen)}
                onKeyDown={() => {}}
              >
                {sideMenuOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              </div>
              <div className="site-header-content">

                {/* Show feedback buttons in dropdown when screen is too narrow */}
                { feedbackButtonsInHeader && (
                <div id="feedback-header-buttons">
                  <Button
                    type="secondary"
                    icon={<BugOutlined />}
                    onClick={onReportBugClicked}
                  >
                    Report a bug
                  </Button>
                </div>
                ) }

                {/* Dont show social links on mobile */}
                {/* { !isMobile && <HeaderSocialLinks /> } */}

                {/* Disable viewing recent activity for external users */}
                { !currentUser.external && <RecentActivity />}

                <HeaderDropdownOptions
                  showFeedbackButtons={!feedbackButtonsInHeader}
                  onReportBugClicked={onReportBugClicked}
                  showLegacyPages={showLegacyPages}
                  onToggleLegacy={onToggleLegacy}
                />
              </div>
            </Header>

            <div id="page-content">
              <BirthdayAnnouncement
                forceClose={location.pathname !== '/dashboard/home'}
              />
              {pageHeaderDetails && (
              <PageHeader
                className="page-header"
                title={pageHeaderDetails.title}
                onBack={pageHeaderDetails.backPath ? (
                  () => history.push(pageHeaderDetails.backPath)
                ) : null}
                subTitle={pageHeaderDetails.description}
              />
              )}
              {/* TODO: Hide tabs if the user does not have access to the page */}
              {currentRoute?.tabs && (
              <Tabs
                className="page-tabs"
                onChange={setActiveTab}
                activeKey={activeTab}
                tabBarExtraContent={tabBarExtra}
              >
                {currentRoute.tabs.map(({ label, id }) => (
                  <TabPane
                    tab={label}
                    key={id}
                  />
                ))}
              </Tabs>
              )}
              <PageTabsProvider activeTab={activeTab} setTabBarExtra={setTabBarExtra}>
                <PageContent
                  currentUser={currentUser}
                  permissions={permissions || []}
                  hideFooter={!!currentRoute?.hideFooter}
                />
              </PageTabsProvider>
            </div>
          </Layout>
        </Layout>
      </MiscDataProvider>
    </PermissionGatedRoutesContext.Provider>
  );
};

export default DashboardLayout;
